import _ from "lodash";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "@/reducers/types";
import { KeepOutAreaObjectReadonly } from "@/sx-layout/components/KeepOutAreaObjectReadonly";
import { MachineObjectReadonly } from "@/sx-layout/components/MachineObjectReadonly";
import { PlotPlanDetailArea } from "@/sx-layout/components/PlotPlanDetailArea";
import { useKeepOutAreas } from "@/sx-layout/components/plotmap/hooks/useKeepOutAreas";
import { useMachines } from "@/sx-layout/components/plotmap/hooks/useMachines";
import { MACHINE_ICON_SIZE } from "@/sx-layout/components/plotmap/util/constants";
import { toClip, toDisplayRect } from "@/sx-layout/components/settings/plotPlanMapping/util/plotPlanMappingAdapter";
import { PlotPlanMapping } from "@/sx-layout/hooks/usePlotPlanMappingApi";

type Props = {
  plotPlanMapping: PlotPlanMapping;
  mapRatio: number;
  displayAreaDetail?: boolean;
  onClickPlotPlanDetail?: (id: number, e: React.MouseEvent) => void;
  onDoubleClickPlotPlanDetail?: (id: number) => void;
};

export const PlotPlanDetailObject: FC<Props> = ({
  plotPlanMapping,
  mapRatio,
  displayAreaDetail,
  onClickPlotPlanDetail,
  onDoubleClickPlotPlanDetail,
}) => {
  const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);

  const rect = toDisplayRect(plotPlanMapping);
  const clip = useMemo(() => toClip(plotPlanMapping), [plotPlanMapping]);
  const { machines, refreshMachines } = useMachines(plotPlanMapping.detail_plot_plan_id);
  const { keepOutAreas, refreshKeepOutAreas } = useKeepOutAreas(plotPlanMapping.detail_plot_plan_id);

  const containerRef = useRef<HTMLDivElement>(null);
  const [displayScale, setDisplayScale] = useState({ x: 1, y: 1 });

  const refresh = useMemo(
    () =>
      _.debounce(() => {
        refreshMachines();
        refreshKeepOutAreas();
      }, 300),
    [layoutDate]
  );

  useEffect(() => {
    refresh();
  }, [layoutDate, plotPlanMapping]);

  useEffect(() => {
    if (!containerRef.current) {
      // @ts-ignore: ここはいい
      return;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setDisplayScale({
        x: entry.contentRect.width / clip.width,
        y: entry.contentRect.height / clip.height,
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [clip.width]);

  return (
    <>
      {displayAreaDetail && (
        <PlotPlanDetailArea
          id={`plot-plan-detail-${plotPlanMapping.detail_plot_plan_id}`}
          plotPlanMapping={plotPlanMapping}
          mapRatio={mapRatio}
          onClick={(e) => {
            e.stopPropagation();
            onClickPlotPlanDetail?.(plotPlanMapping.detail_plot_plan_id, e);
          }}
          onDoubleClick={() => onDoubleClickPlotPlanDetail?.(plotPlanMapping.detail_plot_plan_id)}
        />
      )}
      <div
        className="absolute pointer-events-none"
        style={{
          top: rect.top * mapRatio,
          left: rect.left * mapRatio,
          width: rect.width * mapRatio,
          height: rect.height * mapRatio,
          transform: `rotate(${rect.rotation}deg)`,
        }}
      >
        <div className="relative flex h-full w-full items-center justify-center overflow-visible">
          <div ref={containerRef} className="absolute inset-0 overflow-visible">
            {machines.map((data) => {
              const area = keepOutAreas.find((a) => a.keepout_area_id === data.keepout_area_id);

              // 範囲外の重機は表示しない
              if (
                data.x < clip.left ||
                data.y < clip.top ||
                clip.left + clip.width < data.x ||
                clip.top + clip.height < data.y
              ) {
                return null;
              }
              const x = data.x - clip.left;
              const y = data.y - clip.top;

              const size =
                MACHINE_ICON_SIZE * (data.size ?? 1) * Math.min(plotPlanMapping.scale_x, plotPlanMapping.scale_y);

              return (
                <React.Fragment key={`machine-fragment-${data.machine_id}`}>
                  {area && (
                    <KeepOutAreaObjectReadonly
                      id={`area-${area.keepout_area_id}`}
                      area={area}
                      machine={data}
                      style={{
                        position: "absolute",
                        top: Math.round((area.y - clip.top) * displayScale.y),
                        left: Math.round((area.x - clip.left) * displayScale.x),
                        width: (area.w ?? 0) * displayScale.x,
                        height: (area.h ?? 0) * displayScale.y,
                        background: `#${area.icon_color}`,
                      }}
                    />
                  )}
                  <MachineObjectReadonly
                    id={`machine-${data.machine_id}`}
                    machine={data}
                    style={{
                      position: "absolute",
                      top: Math.round(y * displayScale.y) - size / 2,
                      left: Math.round(x * displayScale.x) - size / 2,
                      width: Math.round(size),
                      height: Math.round(size),
                    }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
